import { useCallback, useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { listNodes } from '@/api/nodes'
import { getDefaultConfig, updateDefaultConfig, getNodeConfig, updateNodeConfig } from '@/api/config'
import { useAuth } from '@/auth/useAuth'
import type { NodeSummary, ConfigProfile } from '@/types'
import ConfigEditor from '@/components/ConfigEditor'
import ConfigRevisionHistory from '@/components/ConfigRevisionHistory'
import NodeConfigStatus from '@/components/NodeConfigStatus'
import ConfigHelpPanel from '@/components/ConfigHelpPanel'
import NodeHierarchyPicker from '@/components/NodeHierarchyPicker'

type Tab = 'global' | 'node'

const NODE_PICKER_LIMIT = 1000

function configTab(value: string | null): Tab {
  if (value === 'node') return value
  return 'global'
}

export default function ConfigPage() {
  const { hasPermission } = useAuth()
  const canWriteConfig = hasPermission('config:write')
  const [searchParams, setSearchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<Tab>(
    () => configTab(searchParams.get('tab')),
  )
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [nodesLoading, setNodesLoading] = useState(true)

  // Global
  const [globalConfig, setGlobalConfig] = useState<ConfigProfile | null>(null)
  const [globalLoading, setGlobalLoading] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)

  // Site tier removed from UI — operational decision (Global + Node 2-tier).
  // backend의 site profile API는 그대로 살아있고, 필요해지면 위 Tab union에 'site' 추가.

  // Node
  const [selectedNodeId, setSelectedNodeId] = useState<string>('')
  const [nodeConfig, setNodeConfig] = useState<ConfigProfile | null>(null)
  const [nodeLoading, setNodeLoading] = useState(false)
  const [nodeError, setNodeError] = useState<string | null>(null)

  // Load nodes once
  useEffect(() => {
    listNodes({ limit: NODE_PICKER_LIMIT })
      .then((res) => {
        setNodes(res.items)
        setNodesLoading(false)
      })
      .catch(() => setNodesLoading(false))
  }, [])

  // Load global config when tab is global
  const loadGlobal = useCallback(async () => {
    setGlobalLoading(true)
    setGlobalError(null)
    try {
      const cfg = await getDefaultConfig()
      setGlobalConfig(cfg)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setGlobalError(err?.message ?? 'Failed to load global config')
    } finally {
      setGlobalLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'global') loadGlobal()
  }, [activeTab, loadGlobal])

  // Load node config when node selected
  const loadNode = useCallback(async (nodeId: string) => {
    if (!nodeId) { setNodeConfig(null); return }
    setNodeLoading(true)
    setNodeError(null)
    try {
      const cfg = await getNodeConfig(nodeId)
      setNodeConfig(cfg)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setNodeError(err?.message ?? 'Failed to load node config')
      setNodeConfig(null)
    } finally {
      setNodeLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'node') loadNode(selectedNodeId)
  }, [activeTab, selectedNodeId, loadNode])

  useEffect(() => {
    setActiveTab(configTab(searchParams.get('tab')))
  }, [searchParams])

  function selectTab(tab: Tab) {
    setActiveTab(tab)
    const next = new URLSearchParams(searchParams)
    if (tab === 'global') next.delete('tab')
    else next.set('tab', tab)
    next.delete('node')
    setSearchParams(next, { replace: true })
  }

  if (searchParams.get('tab') === 'guardian-alpha') {
    const legacyNodeId = searchParams.get('node')
    const query = legacyNodeId
      ? `?${new URLSearchParams({ node: legacyNodeId }).toString()}`
      : ''
    return <Navigate to={`/guardian-alpha/config${query}`} replace />
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>Config</h1>

      {/* Outer layout: main area + sticky help panel */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

        {/* Left: tabs + content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e0e0e0' }}>
            {(['global', 'node'] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => selectTab(tab)}
                style={{
                  padding: '8px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #3949ab' : '2px solid transparent',
                  marginBottom: -2,
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: activeTab === tab ? 700 : 400,
                  color: activeTab === tab ? '#3949ab' : '#666',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'global' ? 'Global defaults' : 'Node override'}
              </button>
            ))}
          </div>

          {/* Global tab */}
          {activeTab === 'global' && (
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 340 }}>
                <div style={cardStyle}>
                  <div style={cardTitleStyle}>Global defaults</div>
                  {globalLoading ? (
                    <p style={{ color: '#888', fontSize: 13 }}>Loading...</p>
                  ) : globalError ? (
                    <div>
                      <div style={errStyle}>{globalError}</div>
                      <button onClick={loadGlobal} style={secondaryBtnStyle}>Retry</button>
                    </div>
                  ) : globalConfig ? (
                    <ConfigEditor
                      initialValue={globalConfig.config}
                      revision={globalConfig.revision}
                      readOnly={!canWriteConfig}
                      configScope="global"
                      onSave={async (parsed) => {
                        const res = await updateDefaultConfig({ config: parsed })
                        setGlobalConfig((prev) => prev ? { ...prev, revision: res.revision, config: parsed } : prev)
                        return { revision: res.revision }
                      }}
                    />
                  ) : null}
                </div>
              </div>
              <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {globalConfig && (
                  <ConfigRevisionHistory currentRevision={globalConfig.revision} />
                )}
              </div>
            </div>
          )}

          {/* Site tab removed — Global + Node 2-tier 운영. backend의 site merge 로직은 살아있어 필요해지면 복구 가능. */}

          {/* Node tab */}
          {activeTab === 'node' && (
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 340 }}>
                <div style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={cardTitleStyle}>Node override</div>
                    {nodesLoading ? (
                      <span style={{ fontSize: 12, color: '#aaa' }}>Loading nodes...</span>
                    ) : null}
                  </div>

                  <NodeHierarchyPicker
                    nodes={nodes}
                    nodesLoading={nodesLoading}
                    selectedNodeId={selectedNodeId}
                    onSelectNode={setSelectedNodeId}
                    ariaLabelPrefix="Node override"
                  />

                  {!selectedNodeId ? (
                    <p style={{ color: '#aaa', fontSize: 13 }}>Select a node above to edit its config override.</p>
                  ) : nodeLoading ? (
                    <p style={{ color: '#888', fontSize: 13 }}>Loading...</p>
                  ) : nodeError ? (
                    <div>
                      <div style={errStyle}>{nodeError}</div>
                      <button onClick={() => loadNode(selectedNodeId)} style={secondaryBtnStyle}>Retry</button>
                    </div>
                  ) : nodeConfig ? (
                    <ConfigEditor
                      initialValue={nodeConfig.config}
                      revision={nodeConfig.revision}
                      readOnly={!canWriteConfig}
                      configScope="node"
                      onSave={async (parsed) => {
                        const res = await updateNodeConfig(selectedNodeId, { config: parsed })
                        setNodeConfig((prev) => prev ? { ...prev, revision: res.revision, config: parsed } : prev)
                        return { revision: res.revision }
                      }}
                    />
                  ) : null}
                </div>
              </div>
              <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {nodeConfig && (
                  <ConfigRevisionHistory currentRevision={nodeConfig.revision} />
                )}
                <NodeConfigStatus selectedNodeId={selectedNodeId} />
              </div>
            </div>
          )}

        </div>

        {/* Right: sticky help panel */}
        <ConfigHelpPanel activeTab={activeTab} />
      </div>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '16px',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
  marginBottom: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const errStyle: React.CSSProperties = {
  color: '#c62828',
  fontSize: 13,
  marginBottom: 8,
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
